/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Arrow, Icon } from '../../../globalUtils/globalIcons';
import Select, { ActionMeta, components, SingleValue, ValueContainerProps,StylesConfig } from 'react-select';
import './CustomDropDown.css';
import { CheckBox } from '../../../globalUtils/globalExports';
import { dropDownSelectStyles } from '../../../globalUtils/SelectDropDown/SelectDropDownStyles';
import PropTypes from 'prop-types';

export interface ISelectOption {
  id: string;
  name: string;
  label?: string;
  value?: string;
  icon: string;
}

interface ICustomDropDownProps {
  selectDropDownId: string;
  dropDownClassName: string;
  selectedValue: ISelectOption | null;
  selectOptions: ISelectOption[];
  isDisabled?: boolean;
  handleSelectOptionChange: (value: ISelectOption) => void;
  renderToBody?: boolean;
  placeHolder?: string;
}


// Custom Option component to show icon + label
const CustomOption = (props: any) => {
  const { data, isSelected } = props;
  
  // Check if compact mode (you can pass this via props or className)
  const isCompact = props.selectProps.classNamePrefix?.includes('compact');
  
  if (isCompact) {
    // Compact: show only icon
    return (
      <components.Option {...props}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Icon iconClassName={data?.icon} />
        </div>
      </components.Option>
    );
  }
  
  // Full: show icon + label
  return (
    <components.Option {...props}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Icon iconClassName={data?.icon} />
        <span style={{
          flex: 1,
          wordWrap: 'break-word',
          whiteSpace: 'normal',
          overflowWrap: 'break-word'
        }}>
            {data.label || data.name}
        </span>
      </div>
    </components.Option>
  );
};

// Custom SingleValue component (shows selected value in the control)
const CustomSingleValue = (props: any) => {
  const { data } = props;
  const isCompact = props.selectProps.classNamePrefix?.includes('compact');
  
  if (isCompact) {
    // Compact: show only icon
    return (
      <components.SingleValue {...props}>
        <Icon iconClassName={data?.icon} />
      </components.SingleValue>
    );
  }
  
  // Full: show icon + label
  return (
    <components.SingleValue {...props}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Icon iconClassName={data?.icon} />
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1
        }}>{data.label || data.name}</span>
      </div>
    </components.SingleValue>
  );
};

// Custom Placeholder component
const CustomPlaceholder = (props: any) => {
  const isCompact = props.selectProps.classNamePrefix?.includes('compact');
  
  if (isCompact) {
    return (
      <components.Placeholder {...props}>
        <Icon iconClassName='' />
      </components.Placeholder>
    );
  }
  
  return (
    <components.Placeholder {...props}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Icon iconClassName='' />
        <span>{props.children}</span>
      </div>
    </components.Placeholder>
  );
};

export const CustomDropdownIndicatorPropTypes = {
  isMenuOpen: PropTypes.bool,
  innerProps: PropTypes.object.isRequired
}

type CustomDropdownIndicatorProps = PropTypes.InferProps<
  typeof CustomDropdownIndicatorPropTypes
>

const CustomDropdownIndicator: React.FC<CustomDropdownIndicatorProps> = ({ isMenuOpen }) => {
  const rotateAngle = (isMenuOpen ?? false) ? 180 : 0
  return <Arrow color="black" direction="down" rotateAngle={rotateAngle} />
}

const CustomDropDown: React.FC<ICustomDropDownProps> = ({
  selectDropDownId,
  dropDownClassName,
  selectedValue,
  selectOptions,
  isDisabled = false,
  handleSelectOptionChange,
  renderToBody = false,
  placeHolder = 'Select an option'
}) => {

  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [selected,setSelected] = useState(selectedValue);

  const handleChange = (newValue: SingleValue<ISelectOption>) => {
    if (newValue) {
      setSelected(newValue);
      handleSelectOptionChange(newValue);
    }
  };

  const isCompact = dropDownClassName?.includes('compact');

  const customStyles: StylesConfig<ISelectOption, false> = {
    ...dropDownSelectStyles,

    menuPortal: (base) => ({
      ...base,
      zIndex: 9999999,
      fontFamily: 'var(--font-family)',
      fontSize: '13px'
    }),

    menu: (provided) => ({
      ...dropDownSelectStyles.menu(provided),
      width: '100%',
      zIndex: 9999999,
      fontFamily: 'var(--font-family)',
      fontSize: '13px'
    }),

    control: (provided, state) => ({
      ...dropDownSelectStyles.control(provided, state),
      width: isCompact ? 'auto' : '100%',
      minWidth: isCompact ? 'fit-content' : 'auto',
      padding: 0
    }),

    container: (provided) => ({
      ...dropDownSelectStyles.container(provided),
      width: isCompact ? 'auto' : '100%',
      minWidth: isCompact ? 'fit-content' : 'auto'
    }),

    valueContainer: (provided, state) => ({
      ...dropDownSelectStyles.valueContainer(provided, state),
      padding: isCompact ? '0 2px' : '0px 4px',
      gap: isCompact ? '2px' : '4px',
      maxWidth: isCompact ? 'none' : '90%',
      flexShrink: 0
    }),

    indicatorsContainer: (provided) => ({
      ...dropDownSelectStyles.indicatorsContainer(provided),
      width: isCompact ? '20px' : '30px',
      padding: isCompact ? 0 : undefined,
      flexShrink: 0
    }),

    dropdownIndicator: (provided) => ({
      ...provided,
      padding: isCompact ? '0 2px' : '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    })
  };

  return (
    <div className={'rms__custom__dropdown__wrap ' + dropDownClassName}>
      <Select
        id={selectDropDownId}
        value={selected}
        onChange={handleChange}
        options={selectOptions}
        styles={customStyles}
        placeholder={placeHolder}
        isDisabled={isDisabled}
        isSearchable={isCompact ? false : true}
        classNamePrefix={dropDownClassName}
        components={{
         DropdownIndicator: (props) => (
            <CustomDropdownIndicator {...props}
              isMenuOpen={menuIsOpen}
            />
          ),
          Option: CustomOption,
          SingleValue: CustomSingleValue,
          Placeholder: CustomPlaceholder
        }}
        menuPortalTarget={renderToBody ? document.body : null}
        menuPosition={renderToBody ? 'fixed' : 'absolute'}
        getOptionLabel={(option) => option.label || option.name}
        getOptionValue={(option) => option.id}
        onMenuOpen={() => { setMenuIsOpen(true) }}
        onMenuClose={() => { setMenuIsOpen(false); }}
      />
    </div>
  );
};

export default CustomDropDown;